import React, { useState, useEffect, useRef } from 'react';
import { useSpring, animated as a, config } from 'react-spring';

interface AnimatedCounterProps {
  value: number;
  duration?: number; // duração da animação em ms
  formatter?: (value: number) => string; // formatador opcional
  animationType?: 'easeOut' | 'bounce' | 'gentle' | 'molasses'; // tipo de animação
  delay?: number; // atraso antes de iniciar a animação
  highlightChange?: boolean; // destacar mudanças de valor
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ 
  value, 
  duration = 1500, 
  formatter = (val) => val.toLocaleString(),
  animationType = 'easeOut',
  delay = 0,
  highlightChange = false
}) => {
  const prevValueRef = useRef(value);
  const [displayValue, setDisplayValue] = useState(formatter(value));
  const [highlight, setHighlight] = useState(false);
  
  // Verificar se é um valor monetário
  const isMonetaryValue = formatter(0).includes('R$');
  
  // Configuração da animação baseada no tipo selecionado
  const getAnimationConfig = () => {
    // Para valores monetários, usar uma animação mais suave
    if (isMonetaryValue) {
      return { tension: 120, friction: 14, precision: 0.01 };
    }
    
    switch (animationType) {
      case 'bounce':
        return config.wobbly;
      case 'gentle':
        return config.gentle;
      case 'molasses':
        return config.molasses;
      case 'easeOut':
      default:
        return { tension: 170, friction: 20 };
    }
  };
  
  // Use number animation instead of string interpolation
  const { number } = useSpring({
    from: { number: prevValueRef.current },
    number: value,
    delay,
    config: getAnimationConfig(),
    onChange: ({ value }) => {
      const val = value.number;
      if (!isMonetaryValue) {
        setDisplayValue(formatter(Math.floor(val)));
      } else {
        // Format monetary value
        const formattedValue = formatter(Math.round(val * 100) / 100);
        setDisplayValue(formattedValue);
      }
    },
    onRest: () => {
      if (highlightChange && value !== prevValueRef.current) {
        setHighlight(true);
        setTimeout(() => setHighlight(false), 1000);
      }
      prevValueRef.current = value;
    }
  });
  
  // Update previous value reference when value changes
  useEffect(() => {
    prevValueRef.current = value;
  }, [value]);
  
  // Highlight animation
  const highlightAnim = useSpring({
    transform: highlight ? 'scale(1.05)' : 'scale(1)',
    color: highlight ? '#ffffff' : '#f9fafb',
    textShadow: highlight ? '0 0 8px rgba(255, 255, 255, 0.5)' : '0 0 0px rgba(255, 255, 255, 0)',
    config: { tension: 300, friction: 10 }
  });
  
  // Renderização com animação
  return (
    <a.span 
      className={`animated-counter ${highlight ? 'highlight-change' : ''} ${isMonetaryValue ? 'monetary-value' : ''} ${value >= 1000 ? 'large-value' : ''}`}
      style={highlightAnim}
    >
      {displayValue}
    </a.span>
  );
};

export default AnimatedCounter; 